import React, { useMemo, useRef, useState } from 'react';
import { FormGenericContainer } from '../FormGenericContainer';
import { BubbleAccordion, BubbleButton, BubbleInput } from '../BubbleUI';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { canManageLibraries } from '@go-shared/lib/permissions';
import Fuse from 'fuse.js';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import { safeIdCompare } from '@go-shared/lib/utils';
import { User } from '@go-shared/types/user';
import { intersection } from '@go-shared/lib/functional';
import { BubbleAccordionRef } from '../BubbleUI/BubbleAccordion';
import { Brand } from '@go-shared/types/brand';
import { Property } from '@go-shared/types/property';
import { TextHighlighted } from '@go-client/components/Common/TextHighlighted';
import { BareContentTemplate } from '@go-shared/types/contentTemplate';
import { CanvasContentFormMode } from './CanvasContentForm';
import ContentTemplateFormik, {
  CntTmplFormikExtState,
  ContentTemplateFormikMode,
  ContentTemplateFormikRef,
} from '../Formik/ContentTemplateFormik';
import { BareVenueTemplate, VenueTemplate } from '@go-shared/types/venueTemplate';
import { evaluateAudienceZone } from '@go-shared/lib/audience';
import { isNotBasicContentTemplate } from '@go-shared/lib/contentTemplate';

interface SelectContentPageProps {
  user: User;
  property: Property;
  brand: Brand;
  mode: CanvasContentFormMode;
  contentTemplates: BareContentTemplate[];
  venueTemplates: Array<BareVenueTemplate | VenueTemplate>;
  title?: string;
  selected?: BareContentTemplate;
  onCreate?: (tmpl: BareContentTemplate) => void;
  onSelect?: (tmpl: BareContentTemplate | undefined) => void;
  onGoNext?: () => void;
  onClose?: () => void;
}

export default function SelectContentPage(props: SelectContentPageProps) {
  const { user, selected } = props;

  const [formikMode, setFormikMode] = useState<ContentTemplateFormikMode>({ type: 'filter' });
  const [formikState, setFormikState] = useState<CntTmplFormikExtState | undefined>(undefined);
  const [typeaheadFilterAll, setTypeaheadFilterAll] = useState(false);

  const formikRef = useRef<ContentTemplateFormikRef>(null);
  const accordionRef = useRef<BubbleAccordionRef>(null);

  function handleSelectCntTemplate(contentTmpl: BareContentTemplate | undefined) {
    if (props.onSelect) props.onSelect(contentTmpl);
  }

  async function handleCreateContentTemplate(bareTemplate: BareContentTemplate) {
    if (props.onCreate) {
      props.onCreate(bareTemplate);
      accordionRef.current?.toggle(1);
    }
  }

  function handleCopySelectedCntTemplate() {
    accordionRef.current?.toggle(0);
    setFormikMode({ type: 'create', edit: selected });
    handleSelectCntTemplate(undefined);
  }

  const cntTmplItems = useMemo(() => {
    return props.contentTemplates.map(c => ({
      id: c._id,
      name: c.name,
      description: isNotBasicContentTemplate(c) ? c.description : undefined,
      _template: c,
    }));
  }, [props.contentTemplates]);

  const nameFuse = useMemo(() => {
    return new Fuse(cntTmplItems, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['name'],
      useExtendedSearch: true,
    });
  }, [cntTmplItems]);

  const descFuse = useMemo(() => {
    return new Fuse(cntTmplItems, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['description'],
      useExtendedSearch: true,
    });
  }, [cntTmplItems]);

  const nameFuseSearchResults = useMemo(() => {
    const name = formikState?.values.name;
    if (!name || !nameFuse) return undefined;

    const criteria = `'${name}`;
    return nameFuse.search(criteria).map(s => s.item);
  }, [nameFuse, formikState?.values.name]);

  const descFuseSearchResults = useMemo(() => {
    const description = formikState?.values.descriptionFilter;
    if (!description || !descFuse) return undefined;

    const criteria = `'${description}`;
    return descFuse.search(criteria).map(s => s.item);
  }, [descFuse, formikState?.values.descriptionFilter]);

  let filteredItems = cntTmplItems;

  if (nameFuseSearchResults) {
    filteredItems = intersection(filteredItems, nameFuseSearchResults, 'id');
  }

  if (descFuseSearchResults) {
    filteredItems = intersection(filteredItems, descFuseSearchResults, 'id');
  }

  filteredItems = filteredItems.filter(fi => {
    let meetsTypeCheck = true;
    let meetsAudienceCheck = true;

    meetsTypeCheck = formikState?.values.type
      ? fi._template.type === formikState.values.type
      : true;
    meetsAudienceCheck = formikState?.values.audienceZoneFilter
      ? evaluateAudienceZone(
          'visible',
          formikState.values.audienceZoneFilter,
          fi._template.audience,
          undefined
        )
      : true;

    return meetsTypeCheck && meetsAudienceCheck;
  });

  return (
    <div className="cef-1">
      <FormGenericContainer
        className="absolute top-0 left-0 w-full h-full flex flex-col"
        bodyClassName="p-0-i flex-grow relative bg-eggshell"
        footerClassName="p-4-i border-t"
        footer={() => (
          <>
            {selected && (
              <div className="text-slate-dark">
                <div>
                  Content Selected:
                  <span className="font-semibold"> {selected.name}</span>
                </div>
              </div>
            )}
            <div className="ml-auto" />
            <BubbleButton
              variant="slate"
              inverse
              onClick={() => setTypeaheadFilterAll(!typeaheadFilterAll)}
              icon="filter"
            >
              Typeahead Filter Mode: {typeaheadFilterAll ? 'All' : 'Any'}
            </BubbleButton>
            <BubbleButton variant="navy" disabled={!props.selected} onClick={props.onGoNext}>
              Next: Enter Content Details
            </BubbleButton>
          </>
        )}
      >
        <div
          className={`absolute left-0 top-0 h-full p-6 ${
            selected ? 'overflow-clip' : 'overflow-y-auto overflow-x-hidden'
          }`}
          style={{ width: 'calc(100% - 400px)' }}
        >
          <div className="flex items-start mb-8">
            <div>
              <h1 className="text-lg text-slate-dark font-semibold">
                {props.title || 'Add Content'}
              </h1>
              <h2 className="text-2xl text-navy">1. Select Content</h2>
            </div>
            <BubbleButton
              onClick={props.onClose}
              className="ml-auto"
              variant="navy"
              inverse
              icon="xmark-large"
              iconSize="xl"
            />
          </div>
          <ContentTemplateFormik
            ref={formikRef}
            mode={formikMode}
            skipBasic
            actionsClassName="hidden"
            subnavClassName="-mx-8"
            onStateUpdate={setFormikState}
            onSubmit={handleCreateContentTemplate}
          />
          <div
            className="absolute top-0 left-0 w-full h-full bg-black opacity-25 cursor-pointer transition-transform"
            onClick={() => handleSelectCntTemplate(undefined)}
            style={{
              transform: `translateX(${selected ? '0' : '100%'})`,
            }}
          />
          <div
            className="absolute left-0 bottom-0 z-10 w-full p-6 overflow-y-auto bg-eggshell transition-transform"
            style={{
              height: '90%',
              zIndex: 1,
              transform: `translateX(${selected ? '0' : '100%'})`,
            }}
          >
            <div className="flex items-start mb-8">
              <div>
                <h1 className="text-lg text-slate-dark font-semibold">
                  {selected?.name ?? 'Unknown Template'}
                </h1>
                <h2 className="text-2xl text-navy">Content Details</h2>
              </div>
              <div className="ml-auto" />
              {canManageLibraries(user) && (
                <BubbleButton
                  variant="banana"
                  icon="copy"
                  inverse
                  onClick={handleCopySelectedCntTemplate}
                >
                  Duplicate This Content
                </BubbleButton>
              )}
              <BubbleButton
                onClick={() => handleSelectCntTemplate(undefined)}
                variant="navy"
                inverse
                icon="xmark-large"
                iconSize="xl"
              />
            </div>
            {selected && (
              <ContentTemplateFormik
                mode={{ type: 'view', edit: selected }}
                skipBasic
                subnavClassName="-mx-8"
              />
            )}
          </div>
        </div>

        <div className="absolute right-0 top-0 h-full bg-white border-l" style={{ width: '400px' }}>
          <BubbleAccordion
            ref={accordionRef}
            variant="navy-light"
            singleMode
            noToggle
            headerClassName="px-4 py-4"
            className="h-full flex flex-col"
            open={[1]}
            hideLocked
            locked={canManageLibraries(user) ? [] : [0]}
            labels={['Create a New Content', 'Use an Existing Content']}
            onOpen={idx => {
              if (idx === 0) {
                handleSelectCntTemplate(undefined);
                setFormikMode({ type: 'create' });
              } else {
                setFormikMode({ type: 'filter' });
              }
            }}
            headerFn={(label, open, locked) => (
              <>
                {locked ? (
                  <>
                    <FontAwesomeIcon icon="lock" size="lg" fixedWidth />
                    <span className="font-semibold ml-2-5">{label}</span>
                  </>
                ) : (
                  <>
                    <BubbleInput
                      checked={open}
                      type="checkbox"
                      appearance="rounded"
                      className="mr-3"
                      inputClassName="bg-white"
                    />
                    <span className="font-semibold">{label}</span>
                  </>
                )}
              </>
            )}
          >
            <div className="p-4">
              <div className="text-smoke italic mb-3">
                Complete the new content details by using the form in the left column. Once
                finished, click the button below to create and select this content.
              </div>
              <BubbleButton
                variant="navy"
                disabled={formikState?.disabled ?? true}
                onClick={formikRef?.current?.submit}
              >
                Create & Select New Content
              </BubbleButton>
            </div>
            <div className="p-4">
              <div className="text-smoke italic mb-3">
                Use the form located in the left column to filter existing contents. The list will
                be further filtered as you input information into each field within the form.
              </div>
              <div className="grid grid-cols-1">
                {filteredItems.map(itm => (
                  <div className="relative py-1" key={itm.id}>
                    <BubbleInput
                      type="checkbox"
                      className="mr-2"
                      inputClassName="stretched-link bg-white"
                      checked={safeIdCompare(selected?._id, itm._template._id)}
                      appearance="rounded"
                      onValue={chk => {
                        handleSelectCntTemplate(chk ? itm._template : undefined);
                      }}
                    />
                    <TextHighlighted text={itm.name} />
                  </div>
                ))}
              </div>
            </div>
          </BubbleAccordion>
        </div>
      </FormGenericContainer>
    </div>
  );
}
